"use strict";
import { CurrencyAmount, Token, Ether, Price } from "@uniswap/sdk-core";
import { Pair } from "@uniswap/v2-sdk";
export function serializeCurrency(currency) {
  return {
    isNative: currency.isNative,
    isToken: currency.isToken,
    chainId: currency.chainId,
    decimals: currency.decimals,
    symbol: currency.symbol,
    name: currency.name,
    address: currency.isToken ? currency.address : void 0
  };
}
export function deserializeCurrency(serialized) {
  if (serialized.isNative) {
    return Ether.onChain(serialized.chainId);
  } else {
    return new Token(
      serialized.chainId,
      serialized.address,
      serialized.decimals,
      serialized.symbol,
      serialized.name
    );
  }
}
export function serializeCurrencyAmount(amount) {
  return {
    currency: serializeCurrency(amount.currency),
    numerator: amount.numerator.toString(),
    denominator: amount.denominator.toString()
  };
}
export function deserializeCurrencyAmount(serialized) {
  const currency = deserializeCurrency(serialized.currency);
  if (serialized.denominator === "1") {
    return CurrencyAmount.fromRawAmount(currency, serialized.numerator);
  }
  return CurrencyAmount.fromFractionalAmount(
    currency,
    serialized.numerator,
    serialized.denominator
  );
}
export function serializePair(pair) {
  return {
    liquidityToken: serializeCurrency(pair.liquidityToken),
    tokenAmounts: [
      serializeCurrencyAmount(pair.reserve0),
      serializeCurrencyAmount(pair.reserve1)
    ]
  };
}
function deserializeTokenAmount(serialized) {
  const token = deserializeCurrency(serialized.currency);
  if (serialized.denominator === "1") {
    return CurrencyAmount.fromRawAmount(token, serialized.numerator);
  }
  return CurrencyAmount.fromFractionalAmount(token, serialized.numerator, serialized.denominator);
}
export function deserializePair(serialized) {
  const token0Amount = deserializeTokenAmount(serialized.tokenAmounts[0]);
  const token1Amount = deserializeTokenAmount(serialized.tokenAmounts[1]);
  return new Pair(token0Amount, token1Amount);
}
export function serializePositionInfo(position) {
  const base = {
    ...position,
    currency0Amount: serializeCurrencyAmount(position.currency0Amount),
    currency1Amount: serializeCurrencyAmount(position.currency1Amount)
  };
  if (position.totalSupply) {
    base.totalSupply = serializeCurrencyAmount(position.totalSupply);
  }
  if (position.liquidityAmount) {
    base.liquidityAmount = serializeCurrencyAmount(position.liquidityAmount);
  }
  if (position.liquidityToken) {
    base.liquidityToken = serializeCurrency(position.liquidityToken);
  }
  if ("pair" in position && position.pair) {
    base.pair = serializePair(position.pair);
  }
  if ("pool" in position && position.pool) {
    base.pool = {
      fee: position.pool.fee,
      sqrtRatioX96: position.pool.sqrtRatioX96.toString(),
      sqrtPriceX96: position.pool.sqrtRatioX96.toString(),
      // Store both field names for compatibility
      liquidity: position.pool.liquidity.toString(),
      tick: position.pool.tickCurrent,
      // V3Pool uses tickCurrent property
      token0: serializeCurrency(position.pool.token0),
      token1: serializeCurrency(position.pool.token1),
      // Serialize token prices if they exist
      token0Price: position.pool.token0Price ? {
        numerator: position.pool.token0Price.numerator.toString(),
        denominator: position.pool.token0Price.denominator.toString(),
        baseCurrency: serializeCurrency(position.pool.token0Price.baseCurrency),
        quoteCurrency: serializeCurrency(position.pool.token0Price.quoteCurrency)
      } : void 0,
      token1Price: position.pool.token1Price ? {
        numerator: position.pool.token1Price.numerator.toString(),
        denominator: position.pool.token1Price.denominator.toString(),
        baseCurrency: serializeCurrency(position.pool.token1Price.baseCurrency),
        quoteCurrency: serializeCurrency(position.pool.token1Price.quoteCurrency)
      } : void 0
    };
  }
  if ("position" in position && position.position) {
    base.position = void 0;
  }
  return base;
}
export function deserializePositionInfo(serialized) {
  const base = {
    ...serialized,
    currency0Amount: deserializeCurrencyAmount(serialized.currency0Amount),
    currency1Amount: deserializeCurrencyAmount(serialized.currency1Amount)
  };
  if (serialized.totalSupply) {
    base.totalSupply = deserializeCurrencyAmount(serialized.totalSupply);
  }
  if (serialized.liquidityAmount) {
    base.liquidityAmount = deserializeCurrencyAmount(serialized.liquidityAmount);
  }
  if (serialized.liquidityToken) {
    base.liquidityToken = deserializeCurrency(serialized.liquidityToken);
  }
  if (serialized.pair) {
    base.pair = deserializePair(serialized.pair);
  }
  if (serialized.pool && typeof serialized.pool === "object") {
    const poolData = serialized.pool;
    const token0 = deserializeCurrency(poolData.token0);
    const token1 = deserializeCurrency(poolData.token1);
    if (token0 && token1) {
      const poolObj = {
        fee: poolData.fee,
        liquidity: poolData.liquidity,
        tick: poolData.tick,
        token0,
        token1
      };
      if (poolData.sqrtRatioX96) {
        poolObj.sqrtRatioX96 = poolData.sqrtRatioX96;
      }
      if (poolData.sqrtPriceX96) {
        poolObj.sqrtPriceX96 = poolData.sqrtPriceX96;
      }
      if (poolData.token0Price && poolData.token0Price.baseCurrency && poolData.token0Price.quoteCurrency) {
        const baseCurrency = deserializeCurrency(poolData.token0Price.baseCurrency);
        const quoteCurrency = deserializeCurrency(poolData.token0Price.quoteCurrency);
        poolObj.token0Price = new Price(
          baseCurrency,
          quoteCurrency,
          poolData.token0Price.denominator,
          poolData.token0Price.numerator
        );
      }
      if (poolData.token1Price && poolData.token1Price.baseCurrency && poolData.token1Price.quoteCurrency) {
        const baseCurrency = deserializeCurrency(poolData.token1Price.baseCurrency);
        const quoteCurrency = deserializeCurrency(poolData.token1Price.quoteCurrency);
        poolObj.token1Price = new Price(
          baseCurrency,
          quoteCurrency,
          poolData.token1Price.denominator,
          poolData.token1Price.numerator
        );
      }
      base.pool = poolObj;
    }
  }
  return base;
}
export function serializeLiquidityTxContext(txContext) {
  if (!txContext) return txContext;
  const serialized = { ...txContext };
  if (txContext.action) {
    serialized.action = {
      ...txContext.action,
      currency0Amount: txContext.action.currency0Amount ? serializeCurrencyAmount(txContext.action.currency0Amount) : void 0,
      currency1Amount: txContext.action.currency1Amount ? serializeCurrencyAmount(txContext.action.currency1Amount) : void 0,
      liquidityToken: txContext.action.liquidityToken ? serializeCurrency(txContext.action.liquidityToken) : void 0
    };
  }
  return serialized;
}
export function deserializeLiquidityTxContext(serialized) {
  if (!serialized) return serialized;
  const deserialized = { ...serialized };
  if (serialized.action) {
    const currency0Amount = serialized.action.currency0Amount;
    const currency1Amount = serialized.action.currency1Amount;
    deserialized.action = {
      ...serialized.action,
      currency0Amount: currency0Amount ? deserializeCurrencyAmountFromAny(currency0Amount) : void 0,
      currency1Amount: currency1Amount ? deserializeCurrencyAmountFromAny(currency1Amount) : void 0,
      liquidityToken: serialized.action.liquidityToken ? deserializeCurrency(serialized.action.liquidityToken) : void 0
    };
  }
  return deserialized;
}
function deserializeCurrencyAmountFromAny(data) {
  if (data.numerator && data.denominator && Array.isArray(data.numerator) && Array.isArray(data.denominator)) {
    const currency = deserializeCurrency(data.currency);
    const numeratorStr = data.numerator.join("");
    const denominatorStr = data.denominator.join("");
    return CurrencyAmount.fromFractionalAmount(currency, numeratorStr, denominatorStr);
  }
  if (typeof data.numerator === "string" && typeof data.denominator === "string") {
    return deserializeCurrencyAmount(data);
  }
  if (data.quotient && typeof data.quotient.toString === "function") {
    return data;
  }
  throw new Error("Unable to deserialize currency amount from format: " + JSON.stringify(data));
}
